import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { ChecklistItem, SegmentInspectionRecord } from '../segment-inspection.types';

@Component({
  selector: 'app-segment-inspection-detail',
  templateUrl: './segment-inspection-detail.component.html',
  styleUrls: ['./segment-inspection-detail.component.css'],
})
export class SegmentInspectionDetailComponent implements OnInit {
  id!: number;
  loading = true;
  saving = false;
  record: SegmentInspectionRecord | null = null;
  staff: { id: number; full_name: string }[] = [];

  documentChecklist: ChecklistItem[] = [];
  qaChecklist: ChecklistItem[] = [];
  materialChecklist: ChecklistItem[] = [];

  declareForm: FormGroup;
  nominateForm: FormGroup;
  completeForm: FormGroup;

  geoLoading = false;
  geoError = '';

  readonly statusSteps = [
    { key: 'draft', label: 'Create' },
    { key: 'ready_for_inspection', label: 'Supervisor verify' },
    { key: 'engineer_nominated', label: 'Engineer assigned' },
    { key: 'inspection_completed', label: 'Site inspection' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private toastr: ToastrMsgService,
    private fb: FormBuilder
  ) {
    this.declareForm = this.fb.group({
      planned_inspection_date: ['', Validators.required],
      work_status_summary: [''],
      notes: [''],
    });
    this.nominateForm = this.fb.group({
      inspection_engineer_id: [null, Validators.required],
      planned_inspection_date: [''],
      notes: [''],
    });
    this.completeForm = this.fb.group({
      findings_summary: ['', Validators.required],
      compliance_result: ['pass', Validators.required],
      material_consumption_approved: [true],
      notes: [''],
    });
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));
    this.load();
    this.api.getStaff({ keyword: '' }).subscribe({
      next: (res: { data?: { id: number; full_name: string }[] }) => {
        this.staff = res.data || (res as unknown as { id: number; full_name: string }[]) || [];
      },
    });
  }

  load(): void {
    this.loading = true;
    this.api.getSegmentInspectionById(this.id).subscribe({
      next: (res: { data?: SegmentInspectionRecord }) => {
        this.record = this.normalize(res.data);
        this.hydrateForms();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err?.error?.message || 'Load failed');
      },
    });
  }

  private normalize(raw?: SegmentInspectionRecord): SegmentInspectionRecord | null {
    if (!raw) return null;
    const parse = (v: unknown): ChecklistItem[] => {
      if (Array.isArray(v)) return v as ChecklistItem[];
      if (typeof v === 'string') {
        try { return JSON.parse(v); } catch { return []; }
      }
      return [];
    };
    return {
      ...raw,
      document_checklist: parse(raw.document_checklist),
      qa_checklist: parse(raw.qa_checklist),
      material_consumption_checklist: parse(raw.material_consumption_checklist),
      workflow_history: Array.isArray(raw.workflow_history) ? raw.workflow_history : [],
    };
  }

  private hydrateForms(): void {
    if (!this.record) return;
    this.documentChecklist = [...this.record.document_checklist];
    this.qaChecklist = [...this.record.qa_checklist];
    this.materialChecklist = [...this.record.material_consumption_checklist];

    this.declareForm.patchValue({
      planned_inspection_date: this.record.planned_inspection_date || '',
      work_status_summary: this.record.work_status_summary || '',
    });
    this.nominateForm.patchValue({
      inspection_engineer_id: this.record.inspection_engineer_id || null,
      planned_inspection_date: this.record.planned_inspection_date || '',
    });
  }

  stepIndex(): number {
    const s = this.record?.status || 'draft';
    if (s === 'draft' || s === 'rejected') return 0;
    if (s === 'ready_for_inspection') return 1;
    if (s === 'engineer_nominated') return 2;
    return 3;
  }

  canDeclareReady(): boolean {
    return !!this.record && ['draft', 'rejected'].includes(this.record.status);
  }

  canNominate(): boolean {
    return this.record?.status === 'ready_for_inspection';
  }

  canComplete(): boolean {
    return this.record?.status === 'engineer_nominated';
  }

  isFinished(): boolean {
    return !!this.record && ['inspection_completed', 'approved', 'rejected'].includes(this.record.status);
  }

  toggleChecklist(list: ChecklistItem[], item: ChecklistItem): void {
    item.checked = !item.checked;
  }

  declareReady(): void {
    if (this.declareForm.invalid) {
      this.declareForm.markAllAsTouched();
      return;
    }
    this.saving = true;
    const v = this.declareForm.value;
    this.api
      .declareSegmentInspectionReady(this.id, {
        document_checklist: this.documentChecklist,
        planned_inspection_date: v.planned_inspection_date,
        work_status_summary: v.work_status_summary,
        notes: v.notes,
      })
      .subscribe({
        next: (res: { message?: string }) => {
          this.toastr.success(res.message || 'Declared ready for inspection');
          this.saving = false;
          this.load();
        },
        error: (err) => {
          this.saving = false;
          this.toastr.error(err?.error?.message || 'Failed');
        },
      });
  }

  nominateEngineer(): void {
    if (this.nominateForm.invalid) {
      this.nominateForm.markAllAsTouched();
      return;
    }
    this.saving = true;
    const v = this.nominateForm.value;
    this.api
      .nominateSegmentInspectionEngineer(this.id, {
        inspection_engineer_id: v.inspection_engineer_id,
        planned_inspection_date: v.planned_inspection_date || undefined,
        notes: v.notes,
      })
      .subscribe({
        next: (res: { message?: string }) => {
          this.toastr.success(res.message || 'Engineer notified');
          this.saving = false;
          this.load();
        },
        error: (err) => {
          this.saving = false;
          this.toastr.error(err?.error?.message || 'Failed');
        },
      });
  }

  captureGeo(): void {
    if (!navigator.geolocation) {
      this.geoError = 'Geolocation not supported';
      return;
    }
    this.geoLoading = true;
    this.geoError = '';
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.geoLoading = false;
        if (this.record) {
          this.record.inspection_geo_lat = pos.coords.latitude;
          this.record.inspection_geo_lng = pos.coords.longitude;
        }
        this.toastr.success('Location captured');
      },
      (err) => {
        this.geoLoading = false;
        this.geoError = err.message || 'Could not get location';
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }

  completeInspection(): void {
    if (this.completeForm.invalid) {
      this.completeForm.markAllAsTouched();
      return;
    }
    if (!this.record?.inspection_geo_lat || !this.record?.inspection_geo_lng) {
      this.toastr.error('Capture geo-tagged site location before submitting');
      return;
    }
    this.saving = true;
    const v = this.completeForm.value;
    this.api
      .completeSegmentInspection(this.id, {
        qa_checklist: this.qaChecklist,
        material_consumption_checklist: this.materialChecklist,
        inspection_geo_lat: this.record.inspection_geo_lat,
        inspection_geo_lng: this.record.inspection_geo_lng,
        findings_summary: v.findings_summary,
        compliance_result: v.compliance_result,
        material_consumption_approved: v.material_consumption_approved,
        notes: v.notes,
      })
      .subscribe({
        next: (res: { message?: string }) => {
          this.toastr.success(res.message || 'Inspection completed');
          this.saving = false;
          this.load();
        },
        error: (err) => {
          this.saving = false;
          this.toastr.error(err?.error?.message || 'Failed');
        },
      });
  }

  openMap(): void {
    if (this.record?.map_link) {
      window.open(this.record.map_link, '_blank');
    }
  }

  back(): void {
    void this.router.navigate(['/home/segment-inspections']);
  }

  segmentTitle(): string {
    if (!this.record) return '';
    return this.record.segment_display || this.record.segment_label || `Segment #${this.record.id}`;
  }
}
